<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type {
  InstanceMembership,
  ContentDistributionGroupMembership,
  InstanceAssignmentGroupMembership,
  Collaboration as CollaborationDTO,
} from '@openapi'
import { splitUserName } from '@lib/util/user'
import CUserAvatar from '@lib/components/CUserAvatar/CUserAvatar.vue'
import CCard from '@lib/components/CCard/CCard.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import useThemeVars from '@lib/composables/useThemeVars'

type Collaboration = Pick<CollaborationDTO, 'id' | 'file' | 'role' | 'user' | 'content_distribution_group' | 'folder'>
type Item = Collaboration | InstanceMembership | ContentDistributionGroupMembership | InstanceAssignmentGroupMembership

interface Props {
  items: Item[]
  maxItemsToShow?: number
  hideGroupInfo?: boolean
  title?: string
}
withDefaults(defineProps<Props>(), {
  maxItemsToShow: 5,
})

const { t } = useI18n()
const themeVars = useThemeVars()
</script>

<template>
  <CCard bordered class="pa-2 c-users-access-info">
    <div class="flex flex-col gap-6">
      <div class="flex items-center gap-4">
        <CIcon icon="user" />
        <div class="font-bold">
          {{ title ?? t('canvasUI.components.CUsersAccessInfo.individualUsers', items.length) }}
        </div>
      </div>

      <div class="flex flex-wrap items-center">
        <CUserAvatar
          v-for="{ user } in items.slice(0, maxItemsToShow)"
          :key="user?.id"
          class="c-users-access-info__avatar"
          :firstName="splitUserName(user?.name).first_name"
          :lastName="splitUserName(user?.name).last_name"
        />

        <div v-if="items.length > maxItemsToShow && !hideGroupInfo" class="ml-4 truncate c-users-access-info__more">
          {{ t('canvasUI.components.CUsersAccessInfo.moreOutsideOfGroup', items.length - maxItemsToShow) }}
        </div>
      </div>
    </div>
  </CCard>
</template>

<style lang="scss" scoped>
.c-users-access-info {
  &__more {
    color: v-bind('themeVars.text2');
  }
  &__avatar {
    margin-right: -5px;
    border: 1px solid white;
  }
}
</style>
